import { Box, IconButton, InputBase, Stack, useTheme } from "@mui/material";
import { HelenaSendIcon, StopIcon } from "components/icons";
import { useGlobalStore } from "controller";
import { type FocusEvent, type KeyboardEvent, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import {
  DictationMicButton,
  DictationView,
  dictationIssueMessage,
  playDictationEndSound,
  playDictationStartSound,
  segmentsToText,
  useDictationPushToTalk,
  useVoiceDictation,
} from "shared/features/voice-dictation";
import { type AiDictationAcceptTrigger, trackAiHome } from "../../analytics";

interface ChatComposerProps {
  value: string;
  /** A turn is in flight — the input stays editable, sending is blocked. */
  isBusy?: boolean;
  isStreaming?: boolean;
  /**
   * A proposed change is waiting above. Sending stays OPEN — a reply is the
   * third answer to a card, alongside approve and decline: it withdraws the
   * card and asks Helena for a revised one. The placeholder is what tells the
   * technician that option is there.
   */
  isAwaitingConfirmation?: boolean;
  onChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  /** ✓ on a dictation: the corrected text and the session it came from. */
  onDictationAccept: (text: string, voiceSessionId: string) => void;
}

const isEditable = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT");

const getPlaceholder = (
  isBusy: boolean,
  isAwaitingConfirmation: boolean,
): string => {
  if (isBusy) return "Helena is answering…";
  if (isAwaitingConfirmation) return "Tell Helena what to change…";
  return "Ask anything or tell Helena what to do...";
};

export const ChatComposer = ({
  value,
  isBusy = false,
  isStreaming = false,
  isAwaitingConfirmation = false,
  onChange,
  onSend,
  onStop,
  onDictationAccept,
}: ChatComposerProps) => {
  const theme = useTheme();
  const timezone = useGlobalStore((state) => state.timezone);
  // Leaving the home screen mid-dictation lands the words in the draft, which
  // outlives this component — they're in the composer on return.
  const dictation = useVoiceDictation({
    onLeave: ({ text, voiceSessionId }) =>
      onDictationAccept(text, voiceSessionId),
  });
  const { issue, status: dictationStatus, dismissIssue } = dictation;
  const isDictating = dictationStatus !== "idle";
  const composerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /** The input remounts after ✓ — focus it then, so Cmd/Ctrl+Enter sends straight away. */
  const shouldFocusInputRef = useRef(false);

  // Waits briefly while Soniox finalises the last words; ✗ in that window
  // cancels it and nothing is inserted.
  const handleDictationAccept = async (trigger: AiDictationAcceptTrigger) => {
    shouldFocusInputRef.current = true;
    playDictationEndSound();
    const durationSeconds = dictation.elapsedSeconds;
    const accepted = await dictation.accept();
    if (!accepted) return;
    trackAiHome("ai_dictation_accepted", {
      voice_session_id: accepted.voiceSessionId,
      trigger,
      text_length: accepted.text.length,
      duration_seconds: durationSeconds,
    });
    onDictationAccept(accepted.text, accepted.voiceSessionId);
  };

  const discardDictation = () => {
    playDictationEndSound();
    dictation.discard();
  };

  const handleDictationReject = () => {
    trackAiHome("ai_dictation_rejected", {
      duration_seconds: dictation.elapsedSeconds,
      transcript_length:
        `${segmentsToText(dictation.segments)}${dictation.tail}`.trim().length,
    });
    discardDictation();
  };

  // Push-to-talk, anywhere on the home screen: hold to talk, release = ✓
  // (finalise and insert). Ignored while typing in any other field.
  useDictationPushToTalk({
    enabled: dictation.isAvailable,
    status: dictationStatus,
    canAccept: dictation.canAccept,
    isAllowedTarget: (target) =>
      !isEditable(target) || !!composerRef.current?.contains(target as Node),
    onStart: () => {
      playDictationStartSound();
      void dictation.start();
    },
    onAccept: () => void handleDictationAccept("push_to_talk"),
    onDiscard: discardDictation,
  });
  const canSend = value.trim().length > 0 && !isBusy;

  // Dictation that couldn't start (or died before any words) is back to the
  // normal composer, so the reason goes in a toast. A stream that stopped with
  // words kept shows its reason inside the dictation box instead.
  useEffect(() => {
    if (!issue || dictationStatus !== "idle") return;
    toast.error(dictationIssueMessage(issue, timezone));
    dismissIssue();
  }, [issue, dictationStatus, dismissIssue, timezone]);

  useEffect(() => {
    const input = inputRef.current;
    if (isDictating || !input || !shouldFocusInputRef.current) return;
    shouldFocusInputRef.current = false;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, [isDictating]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" || !(event.metaKey || event.ctrlKey)) return;
    event.preventDefault();
    if (canSend) onSend();
  };

  const handleComposerFocus = (event: FocusEvent<HTMLDivElement>) => {
    const input = inputRef.current;
    if (event.target !== event.currentTarget || !input) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  };

  return (
    /* Gutter wrapper — the gradient border below uses its own padding. */
    <Box ref={composerRef} sx={{ px: "70px" }}>
      {/* Gradient border — same treatment as Helena v2 "Compose with AI". */}
      <Box
        tabIndex={-1}
        onFocus={handleComposerFocus}
        sx={{
          borderRadius: "13px",
          p: "1px",
          outline: "none",
          background: theme.palette.neutral["015"],
          boxShadow: "0px 0px 4px 0px #00000029,  0px 0px 2px 0px #00000014",
          "&:focus-within": {
            background:
              "linear-gradient(90deg, #4D25ED, #874FFF, #FF96E2, #ED25C1, #874FFF, #4D25ED)",
            boxShadow: `0 -8px 24px -12px ${theme.palette.primitive.brand.purple[200]}`,
          },
        }}
      >
        <Stack
          sx={{
            gap: "8px",
            p: "12px 12px 8px",
            borderRadius: "12px",
            backgroundColor: theme.palette.neutral.main,
          }}
        >
          {isDictating ? (
            <DictationView
              status={dictationStatus}
              segments={dictation.segments}
              tail={dictation.tail}
              canAccept={dictation.canAccept}
              issue={issue}
              elapsedSeconds={dictation.elapsedSeconds}
              maxSeconds={dictation.maxSeconds}
              silenceStopIn={dictation.silenceStopIn}
              levelsRef={dictation.levelsRef}
              onAccept={(trigger) => void handleDictationAccept(trigger)}
              onDiscard={handleDictationReject}
            />
          ) : (
            <>
              <InputBase
                inputRef={inputRef}
                multiline
                minRows={2}
                maxRows={6}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={getPlaceholder(isBusy, isAwaitingConfirmation)}
                sx={{
                  ...theme.typography["body-medium"],
                  color: theme.palette.lightBg.high,
                  p: "0 4px",
                }}
              />
              <Stack direction="row" sx={{ alignItems: "center", gap: "8px" }}>
                {/* Attachments aren't wired up in the stub. */}
                {/* <IconButton
              aria-label="Attach a file"
              sx={{ color: theme.palette.lightBg.low }}
            >
              <AttachIcon sx={{ fontSize: "20px" }} />
            </IconButton> */}
                {dictation.isAvailable && (
                  <DictationMicButton
                    onClick={() => {
                      trackAiHome("ai_dictation_mic_clicked", {});
                      playDictationStartSound();
                      void dictation.start();
                    }}
                    sx={{ ml: "auto", width: "32px", height: "32px" }}
                  />
                )}
                <IconButton
                  aria-label={isStreaming ? "Stop response" : "Send message"}
                  disabled={!(isStreaming || canSend)}
                  onClick={isStreaming ? onStop : onSend}
                  sx={{
                    ml: dictation.isAvailable ? 0 : "auto",
                    width: "32px",
                    height: "32px",
                    color: theme.palette.neutral.main,
                    background: theme.palette.primitive.system.violet[800],
                    ":hover": {
                      background: theme.palette.primitive.system.violet[700],
                    },
                    ":disabled": {
                      color: theme.palette.neutral.main,
                      background: theme.palette.primitive.brand.purple[300],
                    },
                  }}
                >
                  {isStreaming ? (
                    <StopIcon sx={{ fontSize: "24px" }} />
                  ) : (
                    <HelenaSendIcon sx={{ fontSize: "18px" }} />
                  )}
                </IconButton>
              </Stack>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
